import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useUser } from "@/hooks/use-auth";
import { getSupabaseClient } from "@/integrations/supabase/client";
import { getSafeClient } from "@/integrations/supabase/safeClient";

const schema = yup.object({
  website_url: yup.string().url("Must be a valid URL").required("Website URL is required"),
  website_name: yup.string().required("Website Name is required"),
  price_cents: yup.number().integer("Price must be an integer").positive("Price must be positive").required("Price is required"),
  domain_rating: yup.number().integer("DR must be an integer").positive("DR must be positive").max(100, "DR must be between 0 and 100").required("Domain Rating is required"),
  domain_authority: yup.number().integer("DA must be an integer").positive("DA must be positive").max(100, "DA must be between 0 and 100").required("Domain Authority is required"),
  traffic_estimate: yup.number().integer("Traffic must be an integer").positive("Traffic must be positive").required("Traffic Estimate is required"),
  link_type: yup.string().required("Link Type is required"),
  turnaround_days: yup.number().integer("Turnaround Days must be an integer").positive("Turnaround Days must be positive").required("Turnaround Days is required"),
  description: yup.string().required("Description is required"),
  tags: yup.string().required("Tags are required"),
  mask_url: yup.boolean().optional(),
  screenshot_url: yup.string().url("Must be a valid URL").optional().nullable(),
}).required();

type FormData = yup.InferType<typeof schema>;

const CreateOffer = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const user = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, control, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema)
  });

  const onSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    try {
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to create an offer.",
          variant: "destructive",
        });
        return;
      }

      // Convert comma-separated tags string to array
      const tagsArray = formData.tags.split(',').map(tag => tag.trim());

      const offerData = {
        title: formData.website_name,
        price_cents: formData.price_cents,
        seller_profile_id: user.id,
        dr: formData.domain_rating,
        da: formData.domain_authority,
        traffic_estimate: formData.traffic_estimate,
        link_type: formData.link_type,
        turnaround_days: formData.turnaround_days,
        description: formData.description,
        tags: tagsArray,
        created_at: new Date().toISOString(),
        is_active: true,
        masked_url: formData.mask_url || false,
        screenshot_url: formData.screenshot_url || null
      };

      const { error } = await getSupabaseClient().from("offers").insert(offerData);

      if (error) {
        console.error("Error creating offer:", error);
        toast({
          title: "Error",
          description: "Failed to create offer. Please try again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Offer created successfully!",
        });
        navigate("/profile");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Create Backlink Offer | SEO Link Market</title>
      </Helmet>
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold mb-4">Create a New Offer</h1>
          <p className="text-gray-600 mb-6">
            Fill out the form below to create a new offer for your website.
          </p>

          <Card className="w-full max-w-3xl mx-auto">
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <Label htmlFor="website_url">Website URL</Label>
                  <Input
                    id="website_url"
                    type="url"
                    placeholder="https://example.com"
                    {...register("website_url")}
                  />
                  {errors.website_url && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.website_url.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="website_name">Website Name</Label>
                  <Input
                    id="website_name"
                    type="text"
                    placeholder="Example Website"
                    {...register("website_name")}
                  />
                  {errors.website_name && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.website_name.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="price_cents">Price (in cents)</Label>
                  <Input
                    id="price_cents"
                    type="number"
                    placeholder="Enter price in cents"
                    {...register("price_cents", { valueAsNumber: true })}
                  />
                  {errors.price_cents && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.price_cents.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="domain_rating">Domain Rating (DR)</Label>
                    <Input
                      id="domain_rating"
                      type="number"
                      placeholder="Enter DR"
                      {...register("domain_rating", { valueAsNumber: true })}
                    />
                    {errors.domain_rating && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.domain_rating.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="domain_authority">Domain Authority (DA)</Label>
                    <Input
                      id="domain_authority"
                      type="number"
                      placeholder="Enter DA"
                      {...register("domain_authority", { valueAsNumber: true })}
                    />
                    {errors.domain_authority && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.domain_authority.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="traffic_estimate">Traffic Estimate</Label>
                  <Input
                    id="traffic_estimate"
                    type="number"
                    placeholder="Enter traffic estimate"
                    {...register("traffic_estimate", { valueAsNumber: true })}
                  />
                  {errors.traffic_estimate && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.traffic_estimate.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="link_type">Link Type</Label>
                  <Controller
                    control={control}
                    name="link_type"
                    defaultValue=""
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a link type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Guest Post">Guest Post</SelectItem>
                          <SelectItem value="Niche Edit">Niche Edit</SelectItem>
                          <SelectItem value="Homepage Link">Homepage Link</SelectItem>
                          <SelectItem value="Resource Page Link">Resource Page Link</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.link_type && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.link_type.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="turnaround_days">Turnaround Days</Label>
                  <Input
                    id="turnaround_days"
                    type="number"
                    placeholder="Enter turnaround days"
                    {...register("turnaround_days", { valueAsNumber: true })}
                  />
                  {errors.turnaround_days && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.turnaround_days.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Enter description"
                    {...register("description")}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    type="text"
                    placeholder="Enter tags"
                    {...register("tags")}
                  />
                   {errors.tags && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.tags.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="mask_url" {...register("mask_url")} />
                  <Label htmlFor="mask_url">Mask URL</Label>
                </div>

                <div>
                  <Label htmlFor="screenshot_url">Screenshot URL (optional)</Label>
                  <Input
                    id="screenshot_url"
                    type="url"
                    placeholder="Enter screenshot URL"
                    {...register("screenshot_url")}
                  />
                   {errors.screenshot_url && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.screenshot_url.message}
                    </p>
                  )}
                </div>

                <Button disabled={isSubmitting} type="submit" className="w-full">
                  {isSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Create Offer
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default CreateOffer;
